import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Appointment, LoadedUser } from '../../interfaces';
import { getUserNameFromBusiness } from '../../helpers/global';
import useGetAppointmentPayments from '../../hooks/appointments/useGetAppointmentPayments';

type Props = {
  appointment: Appointment;
  loadedUser: LoadedUser | undefined;
};

type ActivityEvent = {
  label: string;
  timestamp: Date;
  detail?: string;
};

const DATE_TIME_FORMAT = "MMM d, yyyy 'at' h:mmaaa";

export function countActivityEvents(appointment: Appointment): number {
  const rescheduleCount =
    (appointment.rescheduledByCust ?? 0) +
    (appointment.rescheduledByEmployee ?? 0);

  return [
    !!appointment.createdAt &&
      appointment.createdByUserId === appointment.customerId,
    !!appointment.acceptedOn,
    !!appointment.scheduledOn,
    !!appointment.confirmedOn,
    !!appointment.depositPaidOn,
    !!appointment.checkoutCompletedOn,
    !!appointment.canceledOn,
    (rescheduleCount > 0 || !!appointment.modifiedBy) &&
      !!appointment.updatedAt,
  ].filter(Boolean).length;
}

function buildActivityEvents(
  appointment: Appointment,
  loadedUser: LoadedUser | undefined,
  checkoutPaymentProcessedByUserId: string | null | undefined,
): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  if (
    appointment.createdAt &&
    appointment.createdByUserId === appointment.customerId
  ) {
    events.push({
      label: 'Request submitted',
      timestamp: new Date(appointment.createdAt),
    });
  }

  if (appointment.acceptedOn) {
    events.push({
      label: 'Request accepted',
      timestamp: new Date(appointment.acceptedOn),
    });
  }

  if (appointment.scheduledOn) {
    const bookedByCustomer =
      appointment.bookerUserId === appointment.customerId;
    const bookerName = getUserNameFromBusiness(
      loadedUser,
      bookedByCustomer,
      appointment.bookerUserId,
      true,
    );
    events.push({
      label: 'Appointment scheduled',
      timestamp: new Date(appointment.scheduledOn),
      detail: bookerName ? `By ${bookerName}` : undefined,
    });
  }

  if (appointment.confirmedOn) {
    events.push({
      label: 'Appointment confirmed',
      timestamp: new Date(appointment.confirmedOn),
    });
  }

  if (appointment.depositPaidOn) {
    events.push({
      label: 'Deposit paid',
      timestamp: new Date(appointment.depositPaidOn),
    });
  }

  if (appointment.checkoutCompletedOn) {
    const processedByCustomer =
      !!checkoutPaymentProcessedByUserId &&
      checkoutPaymentProcessedByUserId === appointment.customerId;
    const processedByName = checkoutPaymentProcessedByUserId
      ? getUserNameFromBusiness(
          loadedUser,
          processedByCustomer,
          checkoutPaymentProcessedByUserId,
          true,
        )
      : '';
    events.push({
      label: 'Appointment checked out',
      timestamp: new Date(appointment.checkoutCompletedOn),
      detail: processedByName ? `By ${processedByName}` : undefined,
    });
  }

  if (appointment.canceledOn) {
    const canceledByCustomer =
      appointment.canceledByUserId === appointment.customerId;
    const canceledByName = getUserNameFromBusiness(
      loadedUser,
      canceledByCustomer,
      appointment.canceledByUserId,
      true,
    );
    events.push({
      label: 'Appointment canceled',
      timestamp: new Date(appointment.canceledOn),
      detail: canceledByName ? `By ${canceledByName}` : undefined,
    });
  }

  const modifiedByName = appointment.modifiedBy
    ? getUserNameFromBusiness(
        loadedUser,
        appointment.modifiedBy === appointment.customerId,
        appointment.modifiedBy,
        true,
      )
    : '';
  const rescheduleCount =
    (appointment.rescheduledByCust ?? 0) +
    (appointment.rescheduledByEmployee ?? 0);
  if (rescheduleCount > 0 && appointment.updatedAt) {
    const countNote =
      rescheduleCount > 1 ? ` (${rescheduleCount} reschedules total)` : '';
    const previously = appointment.previousStartDateTime
      ? `Previously ${format(
          new Date(appointment.previousStartDateTime),
          DATE_TIME_FORMAT,
        )}${countNote}`
      : rescheduleCount > 1
      ? `${rescheduleCount} reschedules`
      : '';
    events.push({
      label: 'Appointment rescheduled',
      timestamp: new Date(appointment.updatedAt),
      detail:
        [previously, modifiedByName && `By ${modifiedByName}`]
          .filter(Boolean)
          .join(' · ') || undefined,
    });
  } else if (appointment.modifiedBy && appointment.updatedAt) {
    events.push({
      label: 'Appointment modified',
      timestamp: new Date(appointment.updatedAt),
      detail: modifiedByName ? `By ${modifiedByName}` : undefined,
    });
  }

  return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

export default function ActivityHistorySection({
  appointment,
  loadedUser,
}: Props) {
  const { payments } = useGetAppointmentPayments(appointment);
  const checkoutPayment = payments?.find(
    payment => payment.type === 'FULL' && payment.status === 'SUCCESS',
  );

  const events = buildActivityEvents(
    appointment,
    loadedUser,
    checkoutPayment?.processedByUserId,
  );

  if (!events.length) {
    return (
      <Typography
        sx={{
          fontSize: 'var(--sidebar-font-sm)',
          color: 'var(--sidebar-tertiary)',
        }}>
        No activity yet
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: '100%',
      }}>
      {events.map(event => (
        <Box
          key={`${event.label}-${event.timestamp.getTime()}`}
          sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'var(--sidebar-border-light)',
              mt: '6px',
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography
              sx={{
                fontSize: 'var(--sidebar-font-base)',
                fontWeight: 600,
                color: 'var(--sidebar-body)',
              }}>
              {event.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--sidebar-font-sm)',
                color: 'var(--sidebar-tertiary)',
              }}>
              {format(event.timestamp, DATE_TIME_FORMAT)}
              {event.detail ? ` — ${event.detail}` : ''}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
